import { useEffect, useRef, useCallback } from "react";
import { Loader2 } from "lucide-react";

export interface InfiniteScrollProps {
    /**
     * 是否正在加载
     */
    loading: boolean;
    /**
     * 是否还有更多数据
     */
    hasMore: boolean;
    /**
     * 加载更多的回调函数
     */
    onLoadMore: () => void;
    /**
     * 距离底部多少像素时触发加载 (默认: 100)
     */
    threshold?: number;
    /**
     * 加载中的文本 (默认: "正在努力加载")
     */
    loadingText?: string;
    /**
     * 没有更多数据时的文本 (默认: "没有更多了")
     */
    noMoreText?: string;
    /**
     * 子元素
     */
    children: React.ReactNode;
    /**
     * 自定义容器类名
     */
    className?: string;
}

/**
 * 无限滚动组件
 * 用于在列表滚动到底部时自动加载更多数据
 */
export const InfiniteScroll = ({
    loading,
    hasMore,
    onLoadMore,
    threshold = 100,
    loadingText = "正在努力加载",
    noMoreText = "没有更多了",
    children,
    className = "",
}: InfiniteScrollProps) => {
    const observerTarget = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const target = entries[0];

            // 当目标元素进入视口，且不在加载中，且还有更多数据时，触发加载
            if (target.isIntersecting && !loadingRef.current && hasMore && !loading) {
                loadingRef.current = true;
                onLoadMore();
            }
        },
        [hasMore, loading, onLoadMore]
    );

    useEffect(() => {
        // 重置加载标记
        if (!loading) {
            loadingRef.current = false;
        }
    }, [loading]);

    useEffect(() => {
        const currentTarget = observerTarget.current;
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: `${threshold}px`,
            threshold: 0.1,
        });

        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [handleObserver, threshold]);

    return (
        <div className={className}>
            {children}

            {/* 加载指示器 */}
            <div
                ref={observerTarget}
                className="flex items-center justify-center py-6 w-full"
            >
                {loading && (
                    <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="animate-spin" size={18} />
                        <span className="text-sm">{loadingText}</span>
                    </div>
                )}

                {!loading && !hasMore && (
                    <div className="text-gray-400 text-sm">
                        {noMoreText}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InfiniteScroll;

